import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'
import type { MaintenanceRequest, ApiResponse } from '@/types'

export const maintenanceKeys = {
  all: ['maintenance_requests'] as const,
  lists: () => [...maintenanceKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...maintenanceKeys.lists(), filters] as const,
  details: () => [...maintenanceKeys.all, 'detail'] as const,
  detail: (id: number) => [...maintenanceKeys.details(), id] as const,
}

export function useMaintenanceRequests(
  page = 1,
  status?: string,
  propertyId?: number,
  category?: string,
  sort?: string,
  sortOrder?: 'asc' | 'desc',
  from?: string,
  to?: string,
  q?: string
) {
  return useQuery({
    queryKey: maintenanceKeys.list({ page, status, propertyId, category, sort, sortOrder, from, to, q }),
    queryFn: async () => {
      const params: Record<string, unknown> = { page }
      if (status) params.status = status
      if (propertyId) params.property_id = propertyId
      if (category) params.category = category
      if (sort) params.sort = sort
      if (sortOrder) params.sort_order = sortOrder
      if (from) params.from = from
      if (to) params.to = to
      if (q) params.q = q
      const { data } = await api.get<ApiResponse<MaintenanceRequest[]>>('/api/maintenance_requests', { params })
      return data
    },
  })
}

export function useMaintenanceRequest(id: number) {
  return useQuery({
    queryKey: maintenanceKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<MaintenanceRequest>>(`/api/maintenance_requests/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

interface CreateMaintenanceRequestInput {
  unit_id: number
  title: string
  description?: string
  category?: string
  category_note?: string
  priority?: string
  customer_id?: number
  image_urls?: string[]
}

export function useCreateMaintenanceRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateMaintenanceRequestInput) => {
      const { unit_id, ...rest } = input
      const { data } = await api.post<ApiResponse<MaintenanceRequest>>(
        `/api/units/${unit_id}/maintenance_requests`,
        { maintenance_request: rest }
      )
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      removeListQueries(queryClient, maintenanceKeys.lists())
    },
  })
}

interface UpdateMaintenanceRequestInput {
  id: number
  title?: string
  description?: string
  category?: string
  category_note?: string
  priority?: string
  status?: string
  notes?: string
  image_urls?: string[]
}

export function useUpdateMaintenanceRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateMaintenanceRequestInput) => {
      const { data } = await api.patch<ApiResponse<MaintenanceRequest>>(
        `/api/maintenance_requests/${id}`,
        { maintenance_request: input }
      )
      return data.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      removeListQueries(queryClient, maintenanceKeys.lists())
      invalidateDetailQueries(queryClient, maintenanceKeys.detail(data.id))
    },
  })
}

export function useDeleteMaintenanceRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/maintenance_requests/${id}`)
      return id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      removeListQueries(queryClient, maintenanceKeys.lists())
    },
  })
}
